import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { useAuth } from "@/context/AuthContext";
import { formatIDR, formatDate, formatAUD } from "@/lib/format";
import { PageHeader, Loading, EmptyState, Field } from "@/components/ui-helpers";
import { StatusBadge } from "@/components/StatusBadge";
import { ArchiveDialog } from "@/components/ArchiveDialog";
import { RowActions } from "@/components/RowActions";
import { PricingBreakdown, breakdownTextLines } from "@/components/PricingBreakdown";
import { CustomerPicker } from "@/components/CustomerPicker";
import { PricingLinesEditor, linesFromOrder, stripLineKeys, validateLines } from "@/components/PricingLinesEditor";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SearchableSelect } from "@/components/SearchableSelect";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Trello, Eye, X, Search, Package, Copy, GitMerge, ClipboardList, Scale, Pencil, AlertTriangle, Lock, Loader2, PencilRuler } from "lucide-react";
import { toast } from "sonner";

// ---------------------------------------------------------------------------
// Product picker (catalogue search)
// ---------------------------------------------------------------------------
function ProductPicker({ tripProducts, onPick }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [dq, setDq] = useState("");
  useEffect(() => { const t = setTimeout(() => setDq(q), 200); return () => clearTimeout(t); }, [q]);

  const results = useMemo(() => {
    const s = dq.trim().toLowerCase();
    if (!s) return tripProducts;
    return tripProducts.filter((p) =>
      ["name", "brand", "category", "variant", "size", "colour", "sku", "product_code"]
        .some((f) => (p[f] || "").toString().toLowerCase().includes(s)));
  }, [dq, tripProducts]);

  return (
    <>
      <Button type="button" size="sm" onClick={() => { setOpen(true); setQ(""); }} data-testid="order-add-product">
        <Search className="h-3.5 w-3.5 mr-1" />Cari Produk
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] flex flex-col p-0">
          <DialogHeader className="p-4 pb-2"><DialogTitle>Pilih Produk</DialogTitle></DialogHeader>
          <div className="px-4 pb-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input autoFocus className="pl-9" placeholder="Cari nama, brand, kategori, varian, ukuran, warna, SKU..."
                value={q} onChange={(e) => setQ(e.target.value)} data-testid="order-product-search" />
            </div>
          </div>
          <div className="flex-1 overflow-y-auto px-4 pb-4 space-y-2" data-testid="order-product-results">
            {results.length === 0 ? (
              <p className="text-center text-sm text-muted-foreground py-8" data-testid="order-product-empty">Produk tidak ditemukan</p>
            ) : results.map((p) => (
              <button type="button" key={p.id} onClick={() => { onPick(p); }} data-testid="order-product-result"
                className="w-full flex items-center gap-3 p-2.5 rounded-xl border border-border bg-card hover:bg-secondary/50 text-left transition-colors">
                <div className="h-14 w-14 rounded-lg bg-secondary flex items-center justify-center overflow-hidden shrink-0">
                  {p.photo ? <img src={p.thumb_url || p.photo} alt={p.name} className="h-full w-full object-cover" /> : <Package className="h-5 w-5 text-muted-foreground" />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{p.name}</p>
                  <p className="text-xs text-muted-foreground truncate">{[p.brand, p.category, p.variant, p.size, p.colour].filter(Boolean).join(" · ")}</p>
                  <div className="flex items-center gap-2 mt-1 flex-wrap">
                    <span className="font-mono text-sm">{formatIDR(p.pricing?.final_price)}</span>
                    {p.discount_active && p.discount_percent > 0 && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-md bg-[hsl(var(--danger-bg))] text-[hsl(var(--danger))]">-{p.discount_percent}%</span>}
                  </div>
                </div>
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

// ---------------------------------------------------------------------------
// Editor helpers
// ---------------------------------------------------------------------------
let _k = 0;
const key = () => `k${++_k}`;

function catalogueItem(p, trip) {
  return {
    _key: key(),
    product_id: p?.id || null, product_name: p?.name || "", photo: p?.thumb_url || p?.photo || null,
    variant: p?.variant || "", quantity: 1, aud_price: p?.aud_price || "0",
    margin_percent: p?.margin_percent || trip?.default_margin_percent || "",
    jasa_titip_type: p?.jasa_titip_type || trip?.default_jasa_titip_type || "fixed",
    jasa_titip_value: p?.jasa_titip_value || trip?.default_jasa_titip_value || "",
    delivery_fee: p?.delivery_fee || trip?.default_delivery_fee || "",
    rounding_method: p?.rounding_method || trip?.default_rounding_method || "none",
    additional_fee: "0", discount: "0",
    promo_active: !!p?.discount_active,
    promo_type: p?.discount_type || "percent",
    promo_percent: Number(p?.discount_percent) || 0,
    promo_amount: Number(p?.discount_amount) || 0,
    is_custom: false,
  };
}

function customItem() {
  return { _key: key(), is_custom: true, product_id: null, product_name: "", variant: "", quantity: 1, aud_price: "0", unit_price_idr: "" };
}

const ITEM_KEYS = ["product_id", "product_name", "photo", "variant", "quantity", "aud_price", "allocated_quantity",
  "margin_percent", "jasa_titip_type", "jasa_titip_value", "delivery_fee", "additional_fee", "discount", "rounding_method",
  "custom_step", "promo_active", "promo_type", "promo_percent", "promo_amount", "override_price", "override_reason",
  "actual_weight_grams", "is_custom", "unit_price_idr"];

function itemsFromOrder(order) {
  return (order.items || []).map((it) => {
    const o = { _key: key() };
    ITEM_KEYS.forEach((k) => { if (it[k] !== undefined) o[k] = it[k]; });
    o.is_custom = !!it.is_custom;
    if (o.is_custom) o.unit_price_idr = String(it.unit_price_idr ?? it.final_price ?? "");
    return o;
  });
}

const stripKeys = stripLineKeys;

// ---------------------------------------------------------------------------
// Order editor — used for BOTH create and edit
// ---------------------------------------------------------------------------
function OrderEditor({ open, setOpen, onSaved, trips, customers, onCustomerCreated, products, selectedTripId, editing }) {
  const { isAdmin } = useAuth();
  const isEdit = !!editing;
  const [trip_id, setTripId] = useState("");
  const [customer_id, setCustomerId] = useState("");
  const [items, setItems] = useState([]);
  const [feeLines, setFeeLines] = useState([]);
  const [discountLines, setDiscountLines] = useState([]);
  const [notes, setNotes] = useState("");
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [saving, setSaving] = useState(false);
  const reqRef = useRef(0);
  const trip = trips.find((t) => t.id === trip_id);

  useEffect(() => {
    if (!open) return;
    if (editing) {
      setTripId(editing.trip_id); setCustomerId(editing.customer_id || "");
      setItems(itemsFromOrder(editing));
      const ln = linesFromOrder(editing);
      setFeeLines(ln.feeLines); setDiscountLines(ln.discountLines);
      setNotes(editing.notes || "");
    } else {
      setItems([]); setCustomerId(""); setNotes(""); setFeeLines([]); setDiscountLines([]);
      setTripId(selectedTripId !== "all" ? selectedTripId : (trips[0]?.id || ""));
    }
    setPreview(null);
  }, [open, editing, selectedTripId, trips]);

  // ---- live pricing preview: SAME calculation the backend uses on save ----
  useEffect(() => {
    if (!open || !trip_id) return;
    const n = ++reqRef.current;
    setPreviewing(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.post("/orders/preview", {
          trip_id, items: stripKeys(items).map((it) => ({ ...it, quantity: Number(it.quantity) || 1 })),
          fee_lines: stripKeys(feeLines), discount_lines: stripKeys(discountLines),
        });
        if (n === reqRef.current) setPreview(data);
      } catch { /* keep last preview */ }
      finally { if (n === reqRef.current) setPreviewing(false); }
    }, 300);
    return () => clearTimeout(t);
  }, [open, trip_id, items, feeLines, discountLines]);

  const tripProducts = products.filter((p) => p.trip_id === trip_id);

  const updItem = (i, k, v) => setItems((its) => its.map((it, idx) => idx === i ? { ...it, [k]: v } : it));
  const rmItem = (i) => setItems((its) => its.filter((_, idx) => idx !== i));
  const pickProduct = (p) => {
    const vv = p.variant || "";
    const idx = items.findIndex((it) => it.product_id === p.id && (it.variant || "") === vv);
    if (idx >= 0) {
      toast("Produk & varian ini sudah ada di order", {
        description: "Tambah jumlah pada item yang sudah ada?",
        action: { label: "Tambah Qty", onClick: () => setItems((its) => its.map((it, i) => i === idx ? { ...it, quantity: Number(it.quantity || 0) + 1 } : it)) },
      });
      return;
    }
    setItems((it) => [...it, catalogueItem(p, trip)]);
    toast.success(`${p.name} ditambahkan`);
  };
  const addCustom = () => setItems((it) => [...it, customItem()]);

  const validate = () => {
    if (!trip_id) return "Pilih trip";
    if (!customer_id) return "Pilih pelanggan";
    if (items.length === 0) return "Tambahkan minimal 1 produk";
    for (const it of items) {
      if (!(it.product_name || "").trim()) return "Nama produk tidak boleh kosong";
      if (!(Number(it.quantity) > 0)) return "Jumlah harus lebih dari 0";
      if (it.is_custom && !(Number(it.unit_price_idr) > 0)) return `Isi harga untuk "${it.product_name}"`;
    }
    return validateLines(feeLines, discountLines);
  };

  const save = async (unlock = false, reason = "") => {
    const err = validate();
    if (err) return toast.error(err);
    const payload = {
      trip_id, customer_id, notes, status: "MENUNGGU KONFIRMASI",
      items: stripKeys(items).map((it) => ({ ...it, quantity: Number(it.quantity) || 1 })),
      fee_lines: stripKeys(feeLines), discount_lines: stripKeys(discountLines),
      unlock, reason,
    };
    // Fully paid order: adding charges creates a new outstanding balance — confirm first.
    if (isEdit && !unlock && editing.payment_status === "Lunas" && preview && preview.grand_total > (editing.order_total || 0)) {
      const diff = preview.grand_total - (editing.order_total || 0);
      if (!window.confirm(`Order ini sudah LUNAS. Perubahan ini menambah tagihan ${formatIDR(diff)} sehingga pelanggan punya sisa tagihan baru. Lanjutkan?`)) return;
    }
    setSaving(true);
    try {
      let r;
      if (isEdit) r = await api.put(`/orders/${editing.id}`, payload);
      else r = await api.post("/orders", payload);
      toast.success(isEdit ? `Order ${r.data.order_number} diperbarui` : `Order ${r.data.order_number} dibuat`, { description: `Total ${formatIDR(r.data.order_total)}` });
      setOpen(false); onSaved(r.data);
    } catch (e) {
      const d = e.response?.data?.detail;
      if (d?.code === "INVOICE_LOCKED") {
        if (!isAdmin) { toast.error("Invoice sudah final — hanya admin yang dapat membuka kunci."); return; }
        const r2 = window.prompt("Invoice pelanggan ini sudah FINAL. Masukkan alasan untuk membuka kunci & menyimpan perubahan:");
        if (r2 && r2.trim()) { setSaving(false); return save(true, r2.trim()); }
        return;
      }
      toast.error((typeof d === "string" ? d : null) || (isEdit ? "Gagal menyimpan perubahan" : "Gagal membuat order"));
    } finally { setSaving(false); }
  };

  const linked = editing?.linked || {};
  const hasLinked = (linked.purchases || 0) > 0 || (linked.deliveries || 0) > 0;
  const pvItems = preview?.items || [];

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!saving) setOpen(v); }}>
      <DialogContent className="max-w-3xl max-h-[94vh] p-0 flex flex-col gap-0 overflow-hidden" data-testid="order-editor-dialog">
        <DialogHeader className="px-5 pt-5 pb-3 border-b border-border">
          <DialogTitle className="flex items-center gap-2 flex-wrap">
            {isEdit ? <><Pencil className="h-4 w-4" />Edit Order {editing.order_number}</> : <><Plus className="h-4 w-4" />Order Baru</>}
            {isEdit && <StatusBadge status={editing.status} />}
            {isEdit && editing.invoice_locked && <Badge variant="outline" className="text-[10px] border-muted-foreground text-muted-foreground"><Lock className="h-3 w-3 mr-1" />Invoice Final</Badge>}
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">
          {isEdit && hasLinked && (
            <div className="rounded-xl border border-[hsl(var(--warning)/0.4)] bg-[hsl(var(--warning-bg))] p-3 text-sm flex items-start gap-2" data-testid="order-edit-linked-warning">
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0 text-[hsl(var(--warning))]" />
              <span>Order ini sudah memiliki {[linked.purchases > 0 && `${linked.purchases} pembelian`, linked.deliveries > 0 && `${linked.deliveries} pengiriman`].filter(Boolean).join(" dan ")}. Pastikan perubahan item tidak bertentangan dengan proses belanja/pengiriman.</span>
            </div>
          )}
          {isEdit && editing.invoice_locked && (
            <div className="rounded-xl border border-border bg-secondary/40 p-3 text-sm flex items-start gap-2" data-testid="order-edit-locked-notice">
              <Lock className="h-4 w-4 mt-0.5 shrink-0 text-muted-foreground" />
              <span>Invoice pelanggan ini sudah <b>final</b>. Perubahan harga hanya bisa disimpan oleh admin dengan alasan buka kunci.</span>
            </div>
          )}

          {/* 1. Pelanggan */}
          <section className="space-y-3">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">1 · Pelanggan</h3>
            <div className="grid sm:grid-cols-[1fr_1.4fr] gap-3">
              <Field label="Trip" required>
                <SearchableSelect value={trip_id} onChange={setTripId} disabled={isEdit}
                  options={trips.map((t) => ({ value: t.id, label: t.name, keywords: [t.name, t.code] }))}
                  placeholder="Pilih trip" searchPlaceholder="Cari trip..." emptyText="Trip tidak ditemukan" testId="order-trip-select" />
              </Field>
              <Field label="Pelanggan" required>
                <CustomerPicker customers={customers} value={customer_id} onChange={setCustomerId} onCustomerCreated={onCustomerCreated} testId="order-customer-select" />
              </Field>
            </div>
          </section>

          {/* 2. Produk */}
          <section className="space-y-3">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">2 · Produk ({items.length})</h3>
              <div className="flex items-center gap-2">
                <ProductPicker tripProducts={tripProducts} onPick={pickProduct} />
                <Button type="button" variant="outline" size="sm" onClick={addCustom} data-testid="order-add-custom"><PencilRuler className="h-3.5 w-3.5 mr-1" />Produk Custom</Button>
              </div>
            </div>
            {items.length === 0 && (
              <div className="rounded-xl border border-dashed border-border p-5 text-center text-sm text-muted-foreground" data-testid="order-items-empty">
                Belum ada produk. Cari dari katalog atau tambahkan produk custom.
              </div>
            )}
            <div className="space-y-2">
              {items.map((it, i) => {
                const pv = pvItems[i];
                return (
                  <div key={it._key} className="rounded-xl border border-border p-3 space-y-2 bg-card" data-testid="order-item-row">
                    <div className="flex items-center gap-2">
                      <div className="h-10 w-10 rounded-lg bg-secondary flex items-center justify-center overflow-hidden shrink-0">
                        {it.photo ? <img src={it.photo} alt={it.product_name} className="h-full w-full object-cover" /> : it.is_custom ? <PencilRuler className="h-4 w-4 text-muted-foreground" /> : <Package className="h-4 w-4 text-muted-foreground" />}
                      </div>
                      <Input className="h-9 text-sm font-medium flex-1" value={it.product_name} onChange={(e) => updItem(i, "product_name", e.target.value)} placeholder={it.is_custom ? "Nama produk custom" : "Nama produk"} data-testid="order-item-name" />
                      {it.is_custom && <Badge variant="outline" className="text-[10px] shrink-0 border-accent text-accent-foreground bg-accent/15" data-testid="order-item-custom-tag">Custom</Badge>}
                      <Button type="button" size="icon" variant="ghost" className="h-8 w-8 text-destructive shrink-0" onClick={() => rmItem(i)} data-testid="order-item-remove"><X className="h-4 w-4" /></Button>
                    </div>
                    {it.is_custom ? (
                      <div className="grid grid-cols-[1fr_1.5fr_1.5fr] gap-2 items-end">
                        <div><label className="text-[11px] text-muted-foreground">Qty</label><Input className="h-9" type="number" min="1" value={it.quantity} onChange={(e) => updItem(i, "quantity", e.target.value)} data-testid="order-item-qty" /></div>
                        <div><label className="text-[11px] text-muted-foreground">Harga / unit (Rp)</label><Input className="h-9" type="number" min="0" step="1000" inputMode="numeric" placeholder="cth. 150000" value={it.unit_price_idr} onChange={(e) => updItem(i, "unit_price_idr", e.target.value)} data-testid="order-item-custom-price" /></div>
                        <div className="text-right pb-1.5">
                          <p className="text-[11px] text-muted-foreground">Subtotal</p>
                          <p className="font-mono text-sm font-semibold" data-testid="order-item-line-total">{formatIDR(pv?.line_total ?? (Number(it.unit_price_idr) || 0) * (Number(it.quantity) || 0))}</p>
                        </div>
                      </div>
                    ) : (
                      <div className="grid grid-cols-[1fr_1.2fr_1fr_1.5fr] gap-2 items-end">
                        <div><label className="text-[11px] text-muted-foreground">Qty</label><Input className="h-9" type="number" min="1" value={it.quantity} onChange={(e) => updItem(i, "quantity", e.target.value)} data-testid="order-item-qty" /></div>
                        <div><label className="text-[11px] text-muted-foreground">AUD</label><Input className="h-9" type="number" step="0.01" value={it.aud_price} onChange={(e) => updItem(i, "aud_price", e.target.value)} data-testid="order-item-aud" /></div>
                        <div><label className="text-[11px] text-muted-foreground">Margin %</label><Input className="h-9" type="number" value={it.margin_percent} onChange={(e) => updItem(i, "margin_percent", e.target.value)} data-testid="order-item-margin" /></div>
                        <div className="text-right pb-1.5">
                          <p className="text-[11px] text-muted-foreground">{pv ? `${formatIDR(pv.unit_price)} × ${pv.quantity}` : "Subtotal"}{pv?.promo_active ? ` · promo -${pv.promo_percent}%` : ""}{it.override_price ? " · override" : ""}</p>
                          <p className="font-mono text-sm font-semibold" data-testid="order-item-line-total">{pv ? formatIDR(pv.line_total) : "—"}</p>
                        </div>
                      </div>
                    )}
                    {!(Number(it.quantity) > 0) && <p className="text-[11px] text-destructive">Jumlah harus lebih dari 0</p>}
                  </div>
                );
              })}
            </div>
          </section>

          {/* 3. Biaya & Diskon */}
          <PricingLinesEditor feeLines={feeLines} setFeeLines={setFeeLines} discountLines={discountLines} setDiscountLines={setDiscountLines}
            title="3 · Biaya & Diskon" testPrefix="order" />

          {/* 4. Catatan */}
          <section className="space-y-2">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">4 · Catatan</h3>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Catatan internal / permintaan pelanggan" rows={2} data-testid="order-notes" />
          </section>
        </div>

        {/* Sticky live total footer — numbers from the same calculation that saves the order */}
        <div className="border-t border-border bg-card px-5 py-3" data-testid="order-editor-footer">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
            <div className="sm:w-80">
              <PricingBreakdown compact subtotal={preview?.items_subtotal ?? 0} feeLines={preview?.fee_lines || []} discountLines={preview?.discount_lines || []} total={preview?.grand_total ?? 0}
                paid={isEdit ? (editing.net_paid ?? editing.total_paid ?? 0) : null}
                balance={isEdit ? (preview?.grand_total ?? 0) - (editing.net_paid ?? editing.total_paid ?? 0) : null}
                testId="order-editor-breakdown" />
              <p className="text-[10px] text-muted-foreground mt-1 h-3">{previewing ? "Menghitung…" : preview ? "Harga dihitung oleh server — sama dengan yang akan disimpan." : ""}</p>
            </div>
            <div className="flex gap-2 justify-end">
              <Button type="button" variant="outline" onClick={() => setOpen(false)} disabled={saving} data-testid="order-cancel-button">Batal</Button>
              <Button type="button" onClick={() => save()} disabled={saving} data-testid="order-save-button">
                {saving && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}{isEdit ? "Simpan Perubahan" : "Simpan Order"}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------
export default function Orders() {
  const { constants, trips, selectedTripId } = useApp();
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [editLoadingId, setEditLoadingId] = useState(null);
  const [statusFilter, setStatusFilter] = useState("");
  const [detail, setDetail] = useState(null);
  const [statusOpen, setStatusOpen] = useState(false);
  const [newStatus, setNewStatus] = useState("");
  const [statusNote, setStatusNote] = useState("");
  const [override, setOverride] = useState(false);
  const [showDeleted, setShowDeleted] = useState(false);
  const [archiveTarget, setArchiveTarget] = useState(null);
  const [mergeMode, setMergeMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [weightOrder, setWeightOrder] = useState(null);
  const [weights, setWeights] = useState([]);
  const [weightSaving, setWeightSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [total, setTotal] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const PAGE_SIZE = 20;

  useEffect(() => { const t = setTimeout(() => setDebouncedSearch(search.trim()), 300); return () => clearTimeout(t); }, [search]);

  const openWeights = (o) => {
    setWeightOrder(o);
    setWeights((o.items || []).map((it, i) => ({
      index: i, name: it.product_name || "Item", variant: it.variant || "", qty: Number(it.quantity || 1),
      grams: it.actual_weight_grams != null ? String(it.actual_weight_grams) : "",
    })));
  };
  const setWeightVal = (idx, v) => setWeights((ws) => ws.map((w) => w.index === idx ? { ...w, grams: v } : w));
  const saveWeights = async () => {
    if (!weightOrder) return;
    setWeightSaving(true);
    try {
      const payload = weights.map((w) => ({ index: w.index, grams: w.grams === "" ? null : Number(w.grams) }));
      await api.patch(`/orders/${weightOrder.id}/item-weights`, { weights: payload });
      toast.success("Berat barang disimpan");
      setWeightOrder(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal menyimpan berat");
    } finally { setWeightSaving(false); }
  };

  const copySummary = async (o) => {
    const lines = [];
    if (o.customer_name) lines.push(`Halo ${o.customer_name},`);
    lines.push(`Berikut ringkasan pesanan Anda (${o.order_number}):`);
    lines.push("");
    (o.items || []).forEach((it, i) => {
      const name = [it.product_name, it.variant].filter(Boolean).join(" - ");
      const qty = it.quantity || 1;
      const lt = it.line_total != null ? it.line_total : (Number(it.final_price || 0) * qty);
      lines.push(`${i + 1}. ${name} x${qty} — ${formatIDR(lt)}`);
    });
    lines.push("");
    lines.push(...breakdownTextLines({
      subtotal: o.items_subtotal, feeLines: o.fee_lines, discountLines: o.discount_lines,
      total: o.order_total, paid: o.net_paid ?? o.total_paid, balance: o.balance,
    }));
    lines.push("");
    lines.push("Terima kasih.");
    try {
      await navigator.clipboard.writeText(lines.join("\n"));
      toast.success("Ringkasan disalin — tinggal tempel di WhatsApp");
    } catch {
      toast.error("Gagal menyalin ringkasan");
    }
  };

  const toggleSelect = (id, v) => setSelectedIds((prev) => (v ? [...prev, id] : prev.filter((x) => x !== id)));
  const doMerge = async () => {
    const sel = orders.filter((o) => selectedIds.includes(o.id));
    if (sel.length < 2) return toast.error("Pilih minimal 2 order");
    if (new Set(sel.map((o) => o.customer_id)).size !== 1) return toast.error("Order harus dari customer yang sama");
    if (new Set(sel.map((o) => o.trip_id)).size !== 1) return toast.error("Order harus dari trip yang sama");
    try {
      const r = await api.post("/orders/merge", { order_ids: selectedIds });
      toast.success(`Order digabung menjadi ${r.data.order_number}`);
      setMergeMode(false); setSelectedIds([]); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal menggabungkan order"); }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { trip_id: selectedTripId, status: statusFilter || undefined, search: debouncedSearch || undefined, include_deleted: isAdmin && showDeleted, page: 1, page_size: PAGE_SIZE };
      const [o, c, p] = await Promise.all([
        api.get("/orders", { params }),
        api.get("/customers"), api.get("/products", { params: { trip_id: selectedTripId } }),
      ]);
      setOrders(o.data.items); setTotal(o.data.total); setHasMore(o.data.has_more); setPage(1);
      setCustomers(c.data); setProducts(p.data.items);
    } finally { setLoading(false); }
  }, [selectedTripId, statusFilter, debouncedSearch, isAdmin, showDeleted]);
  useEffect(() => { load(); }, [load]);

  const loadMore = async () => {
    setLoadingMore(true);
    try {
      const next = page + 1;
      const o = await api.get("/orders", { params: { trip_id: selectedTripId, status: statusFilter || undefined, search: debouncedSearch || undefined, include_deleted: isAdmin && showDeleted, page: next, page_size: PAGE_SIZE } });
      setOrders((prev) => [...prev, ...o.data.items]); setHasMore(o.data.has_more); setPage(next);
    } finally { setLoadingMore(false); }
  };

  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    if (searchParams.get("new") === "1") { setEditing(null); setOpen(true); searchParams.delete("new"); setSearchParams(searchParams, { replace: true }); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const openCreate = () => { setEditing(null); setOpen(true); };
  const openEdit = async (o) => {
    setEditLoadingId(o.id);
    try {
      const r = await api.get(`/orders/${o.id}`);
      if (!r.data.editable) return toast.error("Order yang dibatalkan tidak dapat diubah");
      setEditing(r.data); setOpen(true);
    } catch { toast.error("Gagal memuat order"); }
    finally { setEditLoadingId(null); }
  };
  const onCustomerCreated = (c) => setCustomers((cs) => (cs.find((x) => x.id === c.id) ? cs : [...cs, c].sort((a, b) => (a.name || "").localeCompare(b.name || ""))));

  const openDetail = async (o) => { const r = await api.get(`/orders/${o.id}`); setDetail(r.data); };
  const openStatus = (o) => { setDetail(o); setNewStatus(o.status); setStatusNote(""); setOverride(false); setStatusOpen(true); };

  const doArchive = async () => {
    await api.delete(`/orders/${archiveTarget.id}`);
    toast.success("Order diarsipkan"); load();
  };

  const changeStatus = async () => {
    try {
      await api.post(`/orders/${detail.id}/status`, { status: newStatus, note: statusNote, override });
      toast.success("Status diperbarui"); setStatusOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal ubah status"); }
  };

  return (
    <div>
      <PageHeader title="Order" subtitle="Kelola pesanan pelanggan — satu tempat untuk produk, ongkir, biaya & diskon">
        {isAdmin && (
          <label className="flex items-center gap-2 text-sm text-muted-foreground mr-1 cursor-pointer" data-testid="order-show-deleted">
            <Checkbox checked={showDeleted} onCheckedChange={setShowDeleted} />Tampilkan Terhapus
          </label>
        )}
        <Button variant="outline" onClick={() => navigate("/orders/kanban")} data-testid="orders-kanban-button"><Trello className="h-4 w-4 mr-1.5" />Kanban</Button>
        {isAdmin && <Button variant={mergeMode ? "secondary" : "outline"} onClick={() => { setMergeMode((m) => !m); setSelectedIds([]); }} data-testid="orders-merge-toggle"><GitMerge className="h-4 w-4 mr-1.5" />{mergeMode ? "Batal Gabung" : "Gabungkan"}</Button>}
        <Button variant="outline" onClick={() => navigate("/orders/confirm")} data-testid="orders-confirm-button"><ClipboardList className="h-4 w-4 mr-1.5" />Konfirmasi / Scan</Button>
        <Button onClick={openCreate} data-testid="order-create-button"><Plus className="h-4 w-4 mr-1.5" />Order Baru</Button>
      </PageHeader>

      <div className="mb-4 flex flex-col sm:flex-row gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input className="pl-9" placeholder="Cari no. order atau nama pelanggan..." value={search} onChange={(e) => setSearch(e.target.value)} data-testid="orders-search" />
        </div>
        <Select value={statusFilter || "all"} onValueChange={(v) => setStatusFilter(v === "all" ? "" : v)}>
          <SelectTrigger className="w-full sm:w-56" data-testid="orders-status-filter"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent><SelectItem value="all">Semua Status</SelectItem>{(constants?.order_statuses || []).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      {loading ? <Loading /> : orders.length === 0 ? (
        <EmptyState title={debouncedSearch || statusFilter ? "Tidak ada order yang cocok" : "Belum ada order"} subtitle={debouncedSearch || statusFilter ? "Coba ubah kata kunci atau filter." : "Buat order pertama untuk pelanggan Anda."} action={<Button onClick={openCreate}><Plus className="h-4 w-4 mr-1.5" />Order Baru</Button>} />
      ) : (
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground" data-testid="orders-count">Menampilkan {orders.length} dari {total} order</p>
          {orders.map((o) => {
            const hasLines = (o.fee_lines || []).length > 0 || (o.discount_lines || []).length > 0;
            const hasCustom = (o.items || []).some((it) => it.is_custom);
            return (
              <div key={o.id} className={`rounded-xl border border-border bg-card p-4 shadow-sm ${o.deleted ? "opacity-60 bg-secondary/30" : ""}`} data-testid="order-card">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div className="flex items-start gap-3">
                    {mergeMode && !o.deleted && (
                      <Checkbox checked={selectedIds.includes(o.id)} onCheckedChange={(v) => toggleSelect(o.id, v)} data-testid="order-merge-checkbox" className="mt-1" />
                    )}
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-heading font-semibold">{o.order_number}</p>
                        <StatusBadge status={o.status} />
                        <StatusBadge status={o.payment_status} />
                        {hasCustom && <Badge variant="outline" className="text-[10px] border-accent text-accent-foreground bg-accent/15">Custom</Badge>}
                        {o.deleted && <span className="text-xs text-destructive">Terarsip</span>}
                      </div>
                      <p className="text-sm text-muted-foreground mt-0.5">{o.customer_name} · {o.quantity_total} item · {formatDate(o.order_date)}</p>
                    </div>
                  </div>
                  <div className="text-right flex items-start gap-2">
                    <div>
                      <p className="font-mono font-semibold" data-testid="order-card-total">{formatIDR(o.order_total)}</p>
                      <p className="text-xs text-muted-foreground" data-testid="order-card-balance">Sisa: {formatIDR(o.balance)}</p>
                    </div>
                    {isAdmin && !o.deleted && <RowActions onView={() => openDetail(o)} onArchive={() => setArchiveTarget(o)} testId="order" />}
                  </div>
                </div>
                {hasLines && !o.deleted && (
                  <div className="mt-3 rounded-lg bg-secondary/40 px-3 py-2 sm:w-80 sm:ml-auto">
                    <PricingBreakdown compact subtotal={o.items_subtotal} feeLines={o.fee_lines} discountLines={o.discount_lines} total={o.order_total} testId="order-card-breakdown" />
                  </div>
                )}
                {!o.deleted && (
                  <div className="flex gap-2 mt-3 flex-wrap">
                    <Button size="sm" variant="outline" onClick={() => openDetail(o)} data-testid="order-detail-button"><Eye className="h-3.5 w-3.5 mr-1" />Detail</Button>
                    {o.status !== "DIBATALKAN" && (
                      <Button size="sm" variant="outline" onClick={() => openEdit(o)} disabled={editLoadingId === o.id} data-testid="order-edit-button">
                        {editLoadingId === o.id ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Pencil className="h-3.5 w-3.5 mr-1" />}Edit
                      </Button>
                    )}
                    <Button size="sm" variant="outline" onClick={() => openStatus(o)} data-testid="order-status-button">Ubah Status</Button>
                    <Button size="sm" variant="outline" onClick={() => openWeights(o)} data-testid="order-weight-button"><Scale className="h-3.5 w-3.5 mr-1" />Berat</Button>
                    <Button size="sm" variant="outline" onClick={() => copySummary(o)} data-testid="order-copy-summary-button"><Copy className="h-3.5 w-3.5 mr-1" />Salin Ringkasan</Button>
                  </div>
                )}
              </div>
            );
          })}
          {hasMore && (
            <div className="pt-2 flex justify-center">
              <Button variant="outline" onClick={loadMore} disabled={loadingMore} data-testid="orders-load-more">
                {loadingMore ? "Memuat…" : "Muat lebih banyak"}
              </Button>
            </div>
          )}
        </div>
      )}

      <OrderEditor open={open} setOpen={(v) => { setOpen(v); if (!v) setEditing(null); }} onSaved={() => load()} trips={trips}
        customers={customers} onCustomerCreated={onCustomerCreated} products={products} selectedTripId={selectedTripId} editing={editing} />

      {/* Detail dialog */}
      <Dialog open={!!detail && !statusOpen} onOpenChange={() => setDetail(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="order-detail-dialog">
          {detail && (
            <>
              <DialogHeader><DialogTitle className="flex items-center gap-2 flex-wrap">{detail.order_number} <StatusBadge status={detail.status} /><StatusBadge status={detail.payment_status} /></DialogTitle></DialogHeader>
              <div className="space-y-3">
                <p className="text-sm"><b>Pelanggan:</b> {detail.customer?.name} {detail.customer?.whatsapp ? `(${detail.customer.whatsapp})` : ""}</p>
                <div className="space-y-2">
                  {(detail.items || []).map((it, i) => (
                    <div key={`${it.product_id || it.product_name || "item"}-${i}`} className="rounded-lg border border-border p-2.5 flex justify-between items-center gap-2" data-testid="order-detail-item">
                      <div className="min-w-0">
                        <p className="text-sm font-medium flex items-center gap-1.5 flex-wrap">{it.product_name}{it.variant ? <span className="text-muted-foreground">({it.variant})</span> : null}{it.is_custom && <Badge variant="outline" className="text-[9px] border-accent text-accent-foreground bg-accent/15">Custom</Badge>}</p>
                        <p className="text-xs text-muted-foreground">{it.quantity} × {it.is_custom ? formatIDR(it.final_price) : `${formatAUD(it.aud_price)} → ${formatIDR(it.final_price)}`}{it.breakdown?.overridden && " · (override)"}</p>
                      </div>
                      <p className="font-mono text-sm shrink-0">{formatIDR(it.line_total)}</p>
                    </div>
                  ))}
                </div>
                <div className="rounded-lg bg-secondary/40 p-3">
                  <PricingBreakdown subtotal={detail.items_subtotal} feeLines={detail.fee_lines} discountLines={detail.discount_lines} total={detail.order_total} paid={detail.net_paid} balance={detail.balance} overpaid={detail.overpaid} totalLabel="Total Order" testId="order-detail-breakdown" />
                </div>
                {detail.notes && <p className="text-xs text-muted-foreground">Catatan: {detail.notes}</p>}
                {detail.status_history?.length > 0 && (
                  <div><p className="text-xs font-semibold text-muted-foreground mb-1">Riwayat Status</p>
                    {detail.status_history.map((h) => <div key={h.id} className="text-xs text-muted-foreground flex justify-between"><span>{h.status} · {h.user}</span><span>{formatDate(h.created_at)}</span></div>)}
                  </div>
                )}
                {detail.status !== "DIBATALKAN" && !detail.deleted && (
                  <div className="flex justify-end">
                    <Button size="sm" variant="outline" onClick={() => { const d = detail; setDetail(null); setEditing(d); setOpen(true); }} data-testid="order-detail-edit-button"><Pencil className="h-3.5 w-3.5 mr-1" />Edit Order</Button>
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Status dialog */}
      <Dialog open={statusOpen} onOpenChange={setStatusOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Ubah Status Order</DialogTitle></DialogHeader>
          <Field label="Status Baru">
            <Select value={newStatus} onValueChange={setNewStatus}>
              <SelectTrigger data-testid="status-select"><SelectValue /></SelectTrigger>
              <SelectContent>{(constants?.order_statuses || []).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Catatan (opsional)"><Textarea value={statusNote} onChange={(e) => setStatusNote(e.target.value)} /></Field>
          {isAdmin && (
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={override} onChange={(e) => setOverride(e.target.checked)} data-testid="status-override" />Override aturan (admin)</label>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setStatusOpen(false)}>Batal</Button><Button onClick={changeStatus} data-testid="status-save-button">Simpan</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Weight dialog — update actual per-item weight (grams) for accurate invoices */}
      <Dialog open={!!weightOrder} onOpenChange={(v) => { if (!v) setWeightOrder(null); }}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" data-testid="order-weight-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Scale className="h-4 w-4" />Berat Barang · {weightOrder?.order_number}</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-muted-foreground -mt-1">Isi berat aktual per unit (gram) setelah menimbang. Kosongkan untuk memakai estimasi produk. Berat ini dipakai di invoice.</p>
          <div className="space-y-2">
            {weights.length === 0 && <p className="text-sm text-muted-foreground text-center py-4">Order tidak punya item.</p>}
            {weights.map((w) => (
              <div key={w.index} className="flex items-center gap-3 rounded-lg border border-border p-2.5" data-testid="order-weight-item">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium truncate">{w.name}{w.variant ? ` · ${w.variant}` : ""}</p>
                  <p className="text-[11px] text-muted-foreground">Qty {w.qty}{w.grams !== "" && Number(w.grams) > 0 ? ` · total ${((Number(w.grams) * w.qty) / 1000).toFixed(2)} kg` : ""}</p>
                </div>
                <div className="relative w-28 shrink-0">
                  <Input type="number" min="0" step="1" inputMode="numeric" className="h-9 pr-9 text-right" placeholder="—"
                    value={w.grams} onChange={(e) => setWeightVal(w.index, e.target.value)} data-testid="order-weight-input" />
                  <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">g</span>
                </div>
              </div>
            ))}
          </div>
          {weights.length > 0 && (
            <div className="flex items-center justify-between rounded-lg bg-secondary/40 px-3 py-2 text-sm">
              <span className="text-muted-foreground">Total berat (yang diisi)</span>
              <span className="font-mono font-semibold" data-testid="order-weight-total">
                {(weights.reduce((s, w) => s + (w.grams !== "" ? Number(w.grams) * w.qty : 0), 0) / 1000).toFixed(2)} kg
              </span>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setWeightOrder(null)}>Batal</Button>
            <Button onClick={saveWeights} disabled={weightSaving || weights.length === 0} data-testid="order-weight-save">{weightSaving ? "Menyimpan…" : "Simpan Berat"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ArchiveDialog open={!!archiveTarget} onOpenChange={(v) => { if (!v) setArchiveTarget(null); }}
        title="Arsipkan Order?"
        description={`Order ${archiveTarget?.order_number || ""} akan diarsipkan dan disembunyikan dari daftar serta laporan. Dapat dipulihkan dari Kotak Sampah.`}
        onConfirm={doArchive} testId="order-archive" />

      {mergeMode && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-40 flex items-center gap-3 rounded-full border border-border bg-card shadow-lg px-4 py-2" data-testid="orders-merge-bar">
          <span className="text-sm text-muted-foreground">{selectedIds.length} order dipilih</span>
          <Button size="sm" onClick={doMerge} disabled={selectedIds.length < 2} data-testid="orders-merge-confirm"><GitMerge className="h-4 w-4 mr-1.5" />Gabungkan</Button>
        </div>
      )}
    </div>
  );
}
